import PushNotification from 'react-native-push-notification'
import type {
  PushNotificationScheduleObject,
  PushNotificationScheduledLocalObject,
} from 'react-native-push-notification'
import { Platform } from 'react-native'
import moment, { type Moment } from 'moment'
import I18n from 'APP/I18n'
import makeAsynchronous from 'APP/Helpers/MakeAsynchronous'
import { logger } from 'APP/Lib/Datadog'
import type { Notification, WeekDay } from '@dialogue/services/dist/habits/habits/model'

const REPEAT_TYPES = {
  DAY: 'day',
  WEEK: 'week',
} as const

type RepeatType = (typeof REPEAT_TYPES)[keyof typeof REPEAT_TYPES]

interface NotificationUserInfo {
  habitId: string
  type: string
}

interface ScheduleNotificationConfig {
  date: Moment
  title: string
  message: string
  subtitle: string
  userInfo: NotificationUserInfo
}

export const removeLocalNotificationsForOneHabit = async (habitId?: string): Promise<string[]> => {
  try {
    if (!habitId) {
      throw new Error('habit id is required')
    }

    const allScheduledNotifications = (await makeAsynchronous(
      // @ts-expect-error: getScheduledLocalNotifications uses a typed callback but makeAsynchronous expects (data: unknown) => void
      PushNotification.getScheduledLocalNotifications.bind(PushNotification)
    )) as PushNotificationScheduledLocalObject[]
    const notificationsForHabit = (allScheduledNotifications || []).filter(
      (n) => n?.data?.habitId === habitId
    )

    const ids = notificationsForHabit.map((n) => {
      PushNotification.cancelLocalNotification(n.id)
      return n.id
    })

    return ids
  } catch (err) {
    logger.error('Failed to remove local notifications for habit', {}, err)
    throw err
  }
}

function scheduleLocalNotification(
  { date: requestedDate, title, message, subtitle, userInfo }: ScheduleNotificationConfig,
  repeatType: RepeatType
): void {
  if (![requestedDate, message, userInfo?.habitId, repeatType].every(Boolean)) {
    throw new Error('Missing required params')
  }

  const date = requestedDate.clone()
  //notification can't be scheduled starting from today
  if (date.isBefore(moment(), 'minutes')) {
    date.add(1, repeatType)
  }

  const notification: PushNotificationScheduleObject = {
    channelId: 'habit_channel',
    date: date.toDate(),
    userInfo: { ...userInfo, repeatType },
    // @ts-expect-error this property doesn't exist on the object, but it was already here and I'm wary of removing it
    isTimeZoneAgnostic: true,
    repeatType,
    ...Platform.select({
      ios: {
        title,
        subtitle,
        message,
      },
      // the order it appears on Android
      android: {
        subText: title,
        title: subtitle,
        message,
      },
    }),
  }
  PushNotification.localNotificationSchedule(notification)
}

export const updateLocalNotificationsForOneHabit = async (
  notificationConfig?: Notification
): Promise<void> => {
  try {
    if (!notificationConfig) {
      throw new Error('notificationConfig config is required')
    }

    // 1: CLEAR ALL PREVIOUSLY SCHEDULED NOTIFICATIONS
    await removeLocalNotificationsForOneHabit(notificationConfig.habit.identifier)

    if (!notificationConfig.enabled) return

    // 2: SCHEDULE NEW NOTIFICATIONS
    const config: ScheduleNotificationConfig = {
      title: I18n.t('Habits.notification.title'),
      message:
        notificationConfig.instruction?.[I18n.baseLocale] ||
        I18n.t('Habits.notification.defaultInstruction'),
      subtitle: notificationConfig.habit.title?.[I18n.baseLocale] ?? '',
      userInfo: { habitId: notificationConfig.habit.identifier, type: notificationConfig.type },
      date: moment(notificationConfig.reminder_time, 'HH:mm:ss').local(),
    }

    // @ts-expect-error upstream service casts `week_days` as a single WeekDay while the API returns an array.
    if (!notificationConfig.week_days || notificationConfig.week_days.length === 7) {
      scheduleLocalNotification(config, REPEAT_TYPES.DAY)
    } else {
      // @ts-expect-error upstream service is mistyped (See above comment)
      notificationConfig.week_days.forEach((weekDay: WeekDay) => {
        scheduleLocalNotification(
          { ...config, date: config.date.clone().isoWeekday(weekDay) },
          REPEAT_TYPES.WEEK
        )
      })
    }
  } catch (err) {
    logger.error('Failed to update local notifications for habit', {}, err)
    throw err
  }
}

export const removeLocalNotificationsById = async (notificationId?: string): Promise<string> => {
  try {
    if (!notificationId) {
      throw new Error('notification id is required')
    }

    PushNotification.cancelLocalNotification(notificationId)

    return notificationId
  } catch (err) {
    logger.error('Failed to remove local notification by id', {}, err)
    throw err
  }
}

export const ensureParityWithConfigs = async (
  notificationConfigs?: Notification[]
): Promise<void> => {
  try {
    if (!notificationConfigs) {
      throw new Error('notificationConfigs config is required')
    }

    const allScheduledNotifications = (await makeAsynchronous(
      // @ts-expect-error: getScheduledLocalNotifications uses a typed callback but makeAsynchronous expects (data: unknown) => void
      PushNotification.getScheduledLocalNotifications.bind(PushNotification)
    )) as PushNotificationScheduledLocalObject[]

    // Place to capture all notifications that do align to a config.
    // Used at the end to ensure there weren't any stray scheduled notifications
    const notificationsAccountedFor: PushNotificationScheduledLocalObject[] = []

    // Loop through the configs and ensure all related scheduled notifications match the config
    for (const config of notificationConfigs) {
      // Ignore any configs that aren't enabled
      if (!config.enabled) {
        return
      }

      const notificationsForHabit = (allScheduledNotifications || []).filter(
        (n) => n?.data?.habitId === config.habit.identifier && n?.data?.type === config.type
      )

      // Capture notifications now that we've processed them
      notificationsAccountedFor.push(...notificationsForHabit)

      let diffExists = false

      // Diff 1. No scheduled notifications were found for enabled config
      if (notificationsForHabit.length === 0) {
        diffExists = true
      }

      for (const notification of notificationsForHabit) {
        // Diff 2. The time of at least one notification doesn't match enabled config
        const time = moment(notification.date).format('HH:mm:ss')
        if (time !== config.reminder_time) {
          diffExists = true

          // don't need to check other notifications, cause at least one doesn't match
          break
        }

        // @ts-expect-error upstream service is mistyped (week_days is typed as a single WeekDay but is actually an array)
        const configIsForEveryWeekday = !config.week_days || config.week_days.length === 7

        // Diff 3. Version 1: Config exists for every week day but the notification isn't set to repeat daily
        if (configIsForEveryWeekday && notification?.data?.repeatType !== REPEAT_TYPES.DAY) {
          diffExists = true

          break
        }

        // Diff 3. Version 2: Config exists for some week days but the notification isn't set to repeat weekly
        if (!configIsForEveryWeekday && notification?.data?.repeatType !== REPEAT_TYPES.WEEK) {
          diffExists = true

          break
        }

        // Diff 3. Version 3: Config exists for some week days but the notification doesn't match any of the days
        const day = parseInt(moment(notification.date).format('E'), 10)
        // @ts-expect-error upstream service is mistyped (week_days is typed as a single WeekDay but is actually an array)
        if (!configIsForEveryWeekday && !config.week_days?.includes(day)) {
          diffExists = true

          break
        }
      }

      if (diffExists) {
        await updateLocalNotificationsForOneHabit(config)
      }
    }

    // Diff 4. Config did NOT exist for some notifications
    if (allScheduledNotifications.length !== notificationsAccountedFor.length) {
      allScheduledNotifications.forEach(async (notification) => {
        // Ignore notifications that match a config
        if (notificationsAccountedFor.includes(notification)) {
          return
        }

        // Delete stray notifications
        await removeLocalNotificationsById(notification.id)
      })
    }
  } catch (err) {
    logger.error('Failed to ensure notification parity with configs', {}, err)
    throw err
  }
}
